import React, { useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, object, string } from 'prop-types';
import { compose } from 'redux';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import { translateRoomType, isKnownRoomType } from '../../../util/roomTypeHelpers';

import { AspectRatioWrapper } from '../../../components';

import { categoryLabelId } from '../../../util/categoryDisplay';

import Spinner from '../loadingSpinner/loadingSpinner';
import css from './SearchMapInfoCard.module.css';
import getCountryCodes from '../../../translations/countryCodes';

import ReviewRatings from '../../../containers/ListingPage/ListingPageComponents/ReviewRatings/ReviewRatings';

// Category accent colors — source of truth: new-search-ui/search-page-desktop.html
const categoryColorMap = {
  Bildung: '#2E7BB5',
  Naturschutz: '#2E9E6E',
  Tierschutz: '#C2792F',
  Equality: '#C9543F',
  Health: '#7A5CC4',
  Sports: '#017881',
};

// ListingCard is the listing info without overlayview or carousel controls
const ListingCard = props => {
  const { className, clickHandler, intl, isInCarousel, listing, urlToListing, config } = props;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentListingId, setListingId] = useState('');
  const [lang, setLang] = useState('en');
  const { title, price, publicData } = listing.attributes;
  const { stayTitleDe, stayTitle, experienceTitle, experienceTitleDe, listingType } =
    publicData || {};
  const [imageLoaded, setImageLoaded] = useState(false);
  const isExperienceType = listingType == 'impact-experience';

  useEffect(() => {
    const langMatch = urlToListing.match(/^\/([a-z]{2})\//);
    const lang = langMatch[1];
    setLang(lang);
  }, [urlToListing]);
  const stayTitleMaybe =
    lang === 'en' ? stayTitle : stayTitleDe?.length > 0 ? stayTitleDe : stayTitle;

  const experienceTitleMaybe =
    lang === 'en'
      ? experienceTitle
      : experienceTitleDe?.length > 0
      ? experienceTitleDe
      : experienceTitle;

  //tile
  const titleFirstPart = title.split('•')[0];
  // roomType
  const roomType = listing?.attributes?.publicData?.roomtype;
  //is Experience
  const experienceType = title.split('• ')[1];

  const experienceTitleRaw =
    experienceType === '2 Days 1 Night'
      ? intl.formatMessage({ id: 'experienceTitle.2d1n' })
      : experienceType === '3 Days 2 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.3d2n' })
      : experienceType === '4 Days 3 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.4d3n' })
      : experienceType === '5 Days 4 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.5d4n' })
      : experienceType === '6 Days 5 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.6d5n' })
      : experienceType === '7 Days 6 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.1w' })
      : experienceType === '8 Days 7 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.8d7n' })
      : experienceType === '9 Days 8 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.9d8n' })
      : experienceType === '10 Days 9 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.10d9n' })
      : experienceType === '11 Days 10 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.11d10n' })
      : experienceType === '12 Days 11 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.12d11n' })
      : experienceType === '13 Days 12 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.13d12n' })
      : experienceType === '14 Days 13 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.2w' })
      : experienceType === '15 Days 14 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.15d14n' })
      : experienceType === '16 Days 15 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.16d15n' })
      : experienceType === '17 Days 16 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.17d16n' })
      : experienceType === '18 Days 17 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.18d17n' })
      : experienceType === '19 Days 18 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.19d18n' })
      : experienceType === '20 Days 19 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.20d19n' })
      : experienceType === '21 Days 20 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.3w' })
      : experienceType === '28 Days 27 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.4w' })
      : experienceType === '42 Days 41 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.42d41n' })
      : null;

  const titleSecondpart = isKnownRoomType(roomType)
    ? translateRoomType(roomType, intl)
    : experienceTitle
    ? experienceTitle
    : 'not found';

  const newTitle = isExperienceType
    ? experienceTitleMaybe
      ? experienceTitleMaybe
      : experienceTitleRaw
    : stayTitleMaybe
    ? stayTitleMaybe
    : titleFirstPart + ' • ' + titleSecondpart;

  const formattedPrice =
    price && price.currency === config.currency ? formatMoney(intl, price) : price.currency;
  const listingImages = listing.images && listing.images.length > 0 ? listing.images : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = listingImages
    ? Object.keys(listingImages[0]?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // listing card anchor needs sometimes inherited border radius.
  const classes = classNames(
    css.anchor,
    css.borderRadiusInheritTop,
    { [css.borderRadiusInheritBottom]: !isInCarousel },
    className
  );

  const formatPrice = formattedPrice => {
    let numericPrice = formattedPrice.replace(/[^0-9,\.]/g, '');

    if (numericPrice.indexOf(',') > numericPrice.indexOf('.')) {
      numericPrice = numericPrice.replace(',', '.');
    } else {
      numericPrice = numericPrice.replace(',', '');
    }

    let roundedPrice = Math.ceil(parseFloat(numericPrice));

    if (isNaN(roundedPrice)) {
      console.error('Invalid price format:', formattedPrice);
      return 'N/A';
    }

    return `${roundedPrice}€`;
  };
  let finalPrice = formatPrice(formattedPrice);

  // Category (accent dot + label) — matches the main ListingCard.
  const categories = publicData?.category?.length ? publicData.category : [];
  const categoryRaw = categories[0];

  const categoryColor = categoryColorMap[categoryRaw] || 'var(--sbPrimary)';

  // Localize the raw category slug using the canonical translation key.
  const categoryLabel = categoryRaw
    ? (() => {
        const label = intl.formatMessage({
          id: categoryLabelId(categoryRaw),
          defaultMessage: categoryRaw,
        });
        return label.charAt(0).toUpperCase() + label.slice(1);
      })()
    : '';

  // Location line: "City, Country" localized like the main ListingCard.
  const city = publicData?.city;
  const countriesInLanguage = getCountryCodes(lang);
  const matchingCountry = countriesInLanguage.find(c => c.code === publicData?.country);
  const locationText = `${city || ''}${matchingCountry?.name ? `, ${matchingCountry.name}` : ''}`;

  const handlePrevImage = e => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(
      prevIndex => (prevIndex - 1 + listingImages.length) % listingImages.length
    );
  };

  const handleNextImage = e => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(prevIndex => (prevIndex + 1) % listingImages.length);
  };

  // Swipe support for the image on touch devices. A horizontal swipe cycles the
  // image; `didSwipeRef` then suppresses the card's navigation tap that follows.
  const touchStartXRef = useRef(null);
  const didSwipeRef = useRef(false);
  const handleTouchStart = e => {
    touchStartXRef.current = e.touches[0].clientX;
    didSwipeRef.current = false;
  };
  const handleTouchEnd = e => {
    if (touchStartXRef.current == null || !listingImages || listingImages.length <= 1) {
      return;
    }
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    if (Math.abs(deltaX) > 40) {
      didSwipeRef.current = true;
      setCurrentImageIndex(prevIndex =>
        deltaX < 0
          ? (prevIndex + 1) % listingImages.length
          : (prevIndex - 1 + listingImages.length) % listingImages.length
      );
    }
    touchStartXRef.current = null;
  };

  useEffect(() => {
    if (listing?.id?.uuid !== currentListingId) {
      setListingId(listing?.id?.uuid);
      setCurrentImageIndex(0);
    }
  }, [listing]);

  // Function to format MDE value (extract days and translate to German)
  const formatMDEValue = mdeValue => {
    if (!mdeValue) return '';

    const daysMatch = mdeValue.match(/^(\d+)\s+Days?/i) || mdeValue.match(/^(\d+)\s+days?/i);
    if (!daysMatch) return mdeValue;

    const days = daysMatch[1];
    // Translate "Days" to German "Tage"
    return `${days} ${lang === 'de' ? 'Tage' : 'Days'}`;
  };

  return (
    <a
      alt={title}
      className={classes}
      href={urlToListing}
      onClick={e => {
        e.preventDefault();
        // A horizontal swipe on the image ends in a tap too; don't navigate then.
        if (didSwipeRef.current) {
          didSwipeRef.current = false;
          return;
        }
        // Use clickHandler from props to call internal router
        clickHandler(listing);
      }}
      data-elb="searchp"
      data-elbaction={`click:click map-${listing?.attributes?.title}-uuid-${listing?.id?.uuid}`}
    >
      <div
        className={classNames(css.card, css.borderRadiusInheritTop, {
          [css.borderRadiusInheritBottom]: !isInCarousel,
        })}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
        >
          {!imageLoaded && <Spinner />}
          <img
            src={listingImages[currentImageIndex]?.attributes?.variants['listing-card']?.url}
            className={classNames(css.rootForImage, { [css.imageLoaded]: imageLoaded })}
            alt={title}
            onLoad={() => setImageLoaded(true)}
            style={{ display: imageLoaded ? 'block' : 'none' }}
          />

          {/* Type pill (top-left) — localized like the main ListingCard. */}
          <span className={css.typePill}>
            {isExperienceType ? (
              <FormattedMessage id="ListingCard.typeExperience" defaultMessage="Erlebnisreise" />
            ) : (
              <FormattedMessage id="ListingCard.typeStay" defaultMessage="Unterkunft" />
            )}
          </span>

          {listingImages && listingImages.length > 1 && (
            <>
              <button
                onClick={handlePrevImage}
                className={classNames(css.arrowButton, css.arrowLeft)}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              <button
                onClick={handleNextImage}
                className={classNames(css.arrowButton, css.arrowRight)}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>

              {/* Dot indicators (bottom-center) */}
              <div className={css.dots}>
                {listingImages.map((_, index) => (
                  <span
                    key={index}
                    className={classNames(css.dot, {
                      [css.dotActive]: index === currentImageIndex,
                    })}
                  />
                ))}
              </div>

              {/* Page counter pill (bottom-right) */}
              <span className={css.counter}>
                {currentImageIndex + 1} / {listingImages.length}
              </span>
            </>
          )}
        </AspectRatioWrapper>

        <div className={classNames(css.info, { [css.borderRadiusInheritBottom]: !isInCarousel })}>
          {/* Category dot + label (left) · rating (right) */}
          <div className={css.metaRow}>
            {categoryLabel ? (
              <span className={css.categoryLabel} style={{ color: categoryColor }}>
                <span className={css.categoryDot} style={{ background: categoryColor }} />
                <span className={css.categoryLabelText}>{categoryLabel}</span>
              </span>
            ) : (
              <span className={css.categoryLabel} />
            )}
            {listing?.attributes?.publicData?.location?.reviews ? (
              <span className={css.ratingWrap}>
                <ReviewRatings
                  currentListing={listing}
                  showDot={false}
                  showText={false}
                  isSmall={true}
                  textSize={'1em'}
                  onShowModalFromOutside={() => {}}
                  showReviewsNumber={true}
                />
              </span>
            ) : null}
          </div>

          {/* Title */}
          <div className={css.name}>
            <span className={css.title}>{newTitle}</span>
          </div>

          {/* Location (left) + price tag (right) on a single line */}
          <div className={css.locationPriceRow}>
            {locationText ? (
              <div className={css.location}>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 21s-6-5.5-6-10a6 6 0 1 1 12 0c0 4.5-6 10-6 10z" />
                  <circle cx="12" cy="11" r="2" />
                </svg>
                <span className={css.locationText}>{locationText}</span>
              </div>
            ) : null}

            {/* Price tag: "ab {price} · {Dauer}" */}
            <div className={css.priceInline}>
              {isExperienceType ? <span className={css.priceFrom}>ab</span> : null}
              <span className={css.price}>{finalPrice}</span>
              {isExperienceType && publicData?.MDE ? (
                <span className={css.priceTail}>· {formatMDEValue(publicData.MDE)}</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </a>
  );
};

ListingCard.defaultProps = {
  className: null,
};

ListingCard.propTypes = {
  className: string,
  listing: propTypes.listing.isRequired,
  clickHandler: func.isRequired,
  intl: intlShape.isRequired,
  isInCarousel: bool.isRequired,
};

const SearchMapInfoCard = props => {
  const {
    className,
    rootClassName,
    intl,
    listings,
    createURLToListing,
    onListingInfoCardClicked,
    config,
  } = props;
  const [currentListingIndex, setCurrentListingIndex] = useState(0);

  // A grouped marker holds several listings at the same location. Let the user
  // page through them here — the ListingCard's own arrows only cycle a single
  // listing's images, not the listings in the group.
  const hasCarousel = listings.length > 1;
  const safeIndex = currentListingIndex % listings.length;
  const currentListing = ensureListing(listings[safeIndex]);

  const classes = classNames(rootClassName || css.root, className);
  const caretClass = classNames(css.caret);

  const goToPrev = e => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentListingIndex(prev => (prev + listings.length - 1) % listings.length);
  };
  const goToNext = e => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentListingIndex(prev => (prev + 1) % listings.length);
  };

  return (
    <div className={classes}>
      {/* Global caret classes let the .infoCardPositioner wrapper (a different CSS
          module) flip the caret up/down when the card opens below its pin. */}
      <div className={classNames(css.caretShadow, 'searchMapInfoCardCaretShadow')} />
      <ListingCard
        clickHandler={onListingInfoCardClicked}
        urlToListing={createURLToListing(currentListing)}
        listing={currentListing}
        intl={intl}
        isInCarousel={hasCarousel}
        config={config}
      />
      {hasCarousel ? (
        <div className={classNames(css.groupPagination, css.borderRadiusInheritBottom)}>
          <button
            type="button"
            className={css.groupPageBtn}
            onClick={goToPrev}
            aria-label="Previous listing"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>
          <span className={css.groupPageCount}>{`${safeIndex + 1} / ${listings.length}`}</span>
          <button
            type="button"
            className={css.groupPageBtn}
            onClick={goToNext}
            aria-label="Next listing"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      ) : null}
      <div className={classNames(caretClass, 'searchMapInfoCardCaret')} />
    </div>
  );
};

SearchMapInfoCard.defaultProps = {
  className: null,
  rootClassName: null,
};

SearchMapInfoCard.propTypes = {
  className: string,
  rootClassName: string,
  listings: arrayOf(propTypes.listing).isRequired,
  onListingInfoCardClicked: func.isRequired,
  createURLToListing: func.isRequired,
  config: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(injectIntl)(SearchMapInfoCard);
